import {
    ForwardedRef,
    PropsWithChildren,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
} from 'react';
import { renderToString } from 'react-dom/server';
import { registerPlugin } from 'react-filepond';
import { Button } from '@givebutter/primitives/button';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { useForwardedRef } from '@givebutter/react-util';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UploadContext } from './UploadContext';
import { MediaPreviewPlugin } from 'components/upload/plugins';
import * as Styled from 'components/upload/styled';
import { UploadProps, UploadRef } from 'components/upload/types';
import { UploadLabel } from 'components/upload/upload-label';
import { useUpload } from 'components/upload/useUpload';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import FilePondPluginFilePoster from 'filepond-plugin-file-poster';
import 'filepond-plugin-file-poster/dist/filepond-plugin-file-poster.min.css';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import { twMerge } from 'tailwind-merge';

registerPlugin(
    MediaPreviewPlugin,
    FilePondPluginImageExifOrientation,
    FilePondPluginFileEncode,
    FilePondPluginImagePreview,
    FilePondPluginFilePoster,
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
);

const MAX_FILES = 20;
const ALLOWED_FILE_TYPES = ['image/*', 'video/*'];
const PREVIEW_HEIGHT = 150;
const MAX_SIZE = '5MB';

const Upload = forwardRef((props: UploadProps, ref: ForwardedRef<UploadRef>) => {
    const pondRef = useForwardedRef(ref);
    const { setRef } = useContext(UploadContext);
    const { onReorder, onBeforeAddFile, onError, onWarning, orderFiles, onUpdate, isLoading } =
        useUpload(props as Parameters<typeof useUpload>[0], pondRef);
    const {
        onreorderfiles: _noopReorder,
        beforeAddFile: _noopBeforeAdd,
        onupdatefiles: _noopUpdate,
        uploadLabelText,
        variant = 'multiple',
        labelStyle,
        ...rest
    } = props;

    const isHeadless = variant === 'headless';

    const LabelIdle = useCallback(() => {
        if (isHeadless) {
            return (
                <div
                    className={twMerge(
                        'flex cursor-pointer flex-col items-center justify-center',
                        labelStyle,
                    )}
                >
                    <p className={'!text-sm'}>Click or drag to add an attachment</p>
                    {uploadLabelText}
                </div>
            );
        }

        return <UploadLabel label={uploadLabelText} className={labelStyle} />;
    }, [isHeadless, uploadLabelText, labelStyle]);

    useEffect(() => {
        pondRef.current?._pond.setOptions({
            onupdatefiles: onUpdate,
        });
    }, [onUpdate, pondRef]);

    return (
        <LoadingOverlay isLoading={isLoading}>
            <Styled.FilePond
                ref={ref => {
                    pondRef.current = ref as UploadRef;
                    setRef(pondRef);
                    return pondRef;
                }}
                variant={variant}
                maxFiles={MAX_FILES}
                server={null}
                acceptedFileTypes={ALLOWED_FILE_TYPES}
                imagePreviewHeight={isHeadless ? undefined : PREVIEW_HEIGHT}
                videoPreviewHeight={isHeadless ? undefined : PREVIEW_HEIGHT}
                maxFileSize={MAX_SIZE}
                credits={false}
                allowVideoPreview={true}
                allowMultiple={true}
                allowImageExifOrientation={true}
                allowReorder={true}
                allowFileEncode={true}
                allowImagePreview={!isHeadless}
                onreorderfiles={onReorder}
                beforeAddFile={onBeforeAddFile}
                styleItemPanelAspectRatio={isHeadless ? null : '1'}
                itemInsertLocation={orderFiles}
                onerror={onError}
                onwarning={onWarning}
                labelIdle={renderToString(<LabelIdle />)}
                labelMaxFileSizeExceeded={`The maximum image size is ${props.maxFileSize}, reduce the size and re-upload.`}
                {...rest}
            />
        </LoadingOverlay>
    );
});

const UploadBrowse = () => {
    const { ref, files } = useContext(UploadContext);

    if (files.length > 0) {
        return null;
    }

    return (
        <Button
            variant="none"
            color="primary"
            size={'sm'}
            className="font-medium"
            onClick={() => ref?.current?.browse()}
        >
            <FontAwesomeIcon icon={faPlus} />
            Add attachment
        </Button>
    );
};

const UploadHeadless = (props: PropsWithChildren) => {
    const { files } = useContext(UploadContext);

    return (
        <div
            className={twMerge(
                files.length === 0 && 'sr-only h-0',
                '!mb-0 max-w-[250px] [&_.filepond--file-action-button.filepond--action-remove-item]:cursor-pointer',
            )}
        >
            {props.children}
        </div>
    );
};

export { Upload, UploadBrowse, UploadHeadless };
